import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Typeahead against the curated master category taxonomy (/api/categories/search).
// Resolves common aliases (e.g. "mowing" -> "Lawn Care") to the canonical name.
// Also offers a full scrollable "Browse all categories" list and a "not found"
// state with an explicit Suggest-this-category action (caller decides the flow).
export default function CategoryTypeahead({ value, onChange, onSelect, onNotFound, placeholder, testId, inputClassName = "edit-input" }) {
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [open, setOpen] = useState(false);
  const [browsingAll, setBrowsingAll] = useState(false);
  const [masterGroups, setMasterGroups] = useState(null);
  const [loadingMaster, setLoadingMaster] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    setSearched(false);
    debounceRef.current = setTimeout(() => {
      api.get("/categories/search", { params: { q: value || "" } })
        .then(r => { setResults(r.data.results || []); setSearched(true); })
        .catch(() => { setResults([]); setSearched(true); });
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const loadMaster = () => {
    if (masterGroups) { setBrowsingAll(true); return; }
    setLoadingMaster(true);
    api.get("/categories/master").then(r => { setMasterGroups(r.data.groups || []); setBrowsingAll(true); })
      .finally(() => setLoadingMaster(false));
  };

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const q = (value || "").trim();
    if (!q) return;
    const exact = results.find(r => r.category_name.toLowerCase() === q.toLowerCase());
    if (exact) { onSelect(exact.category_name); setOpen(false); }
  };

  const showNotFound = searched && !browsingAll && (value || "").trim() && results.length === 0;

  return (
    <div ref={boxRef} className="relative">
      <input
        value={value || ""}
        onChange={e => { onChange(e.target.value); setBrowsingAll(false); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={inputClassName}
        autoComplete="off"
        data-testid={testId}
      />
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[240px] bg-white border border-black/10 rounded-xl shadow-lg" data-testid={`${testId}-dropdown`}>
          {browsingAll ? (
            <div className="max-h-72 overflow-y-auto py-1" data-testid={`${testId}-browse-all-list`}>
              <button type="button" onMouseDown={() => setBrowsingAll(false)}
                className="w-full text-left px-3 py-2 text-xs font-semibold text-[#245544] hover:bg-[#F2F4F2] sticky top-0 bg-white border-b border-black/5"
                data-testid={`${testId}-back`}>
                ← Back to search
              </button>
              {(masterGroups || []).map(g => (
                <div key={g.group}>
                  <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#222]/40">{g.group}</div>
                  {g.categories.map(c => (
                    <button key={c.id} type="button" onMouseDown={() => { onSelect(c.category_name); setOpen(false); setBrowsingAll(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#F2F4F2]" data-testid={`${testId}-option-${c.slug}`}>
                      {c.category_name}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <>
              {results.length > 0 && (
                <div className="max-h-64 overflow-y-auto py-1">
                  {results.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      onMouseDown={() => { onSelect(r.category_name); setOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#F2F4F2] flex items-center justify-between gap-2"
                      data-testid={`${testId}-option-${r.slug}`}
                    >
                      <span className="truncate">{r.category_name}</span>
                      <span className="text-[10px] text-[#222]/40 shrink-0">{r.parent_category}</span>
                    </button>
                  ))}
                </div>
              )}
              {showNotFound && (
                <div className="px-3 py-3 text-sm text-[#222]/60 border-b border-black/5" data-testid={`${testId}-not-found`}>
                  No matches for &ldquo;{value.trim()}&rdquo;.
                  {onNotFound && (
                    <button type="button" onMouseDown={() => onNotFound(value.trim())}
                      className="ml-1 text-[#245544] font-semibold underline" data-testid={`${testId}-suggest-link`}>
                      Suggest this category
                    </button>
                  )}
                </div>
              )}
              <button type="button" onMouseDown={loadMaster}
                className="w-full text-left px-3 py-2 text-xs font-semibold text-[#245544] hover:bg-[#F2F4F2]"
                data-testid={`${testId}-browse-all`}>
                {loadingMaster ? "Loading…" : "Browse all categories"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
